import { Copy, CopyCheck } from 'lucide-react';
import { useState, MouseEvent } from 'react';
import DynamicIcon from 'src/shared/components/ui/icons/dynamic-icon';
import { Separator } from 'src/shared/components/ui/separator';
import { PushTextToOutlookAppData } from 'src/features/flows/types/threadStore.types';
import { cn } from 'src/shared/lib/utils';
import { Linkify } from 'src/shared/lib/linkify.utils';

interface CopyButtonInlineProps {
  value: string;
  className?: string;
}

function CopyButtonInline({ value, className }: CopyButtonInlineProps) {
  const [copied, setCopied] = useState(false);

  function handleCopy(e: MouseEvent<HTMLButtonElement>) {
    e.stopPropagation();
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <button
      type="button"
      disabled={!value}
      onClick={handleCopy}
      className={cn(
        'shrink-0 disabled:opacity-50 disabled:pointer-events-none',
        !copied ? 'hover:text-neutral-800 text-neutral-400 transition-all' : '',
        className
      )}
    >
      {!copied ? <Copy size={12} /> : <CopyCheck size={12} className="text-success hover:text-success-800" />}
    </button>
  );
}

interface TextValueCardProps {
  data: PushTextToOutlookAppData;
}

/**
 * Card component for displaying text values pushed from automation flows.
 * Platform-agnostic - works in Outlook plugin, main frontend, and Chrome extension.
 */
export function TextValueCard({ data }: TextValueCardProps) {
  return (
    <div className="flex flex-col border rounded-sm bg-white p-3 gap-2 overflow-y-auto w-full">
      <div className="flex items-center flex-wrap overflow-hidden gap-2 w-full h-6">
        <div className="flex-center shrink-0 rounded-sm w-7 h-full bg-dashboardblue-50">
          <DynamicIcon name="FileText" size={16} className="shrink-0 stroke-2 text-dashboardblue" />
        </div>
        <span className="text-sm font-bold">{data.title}</span>
        <div className="flex-grow" />
        {!!data.value && <CopyButtonInline value={data.value} />}
      </div>
      <Separator />
      {!!data.value && (
        <span className="text-sm break-words whitespace-pre-line">
          <Linkify>{data.value}</Linkify>
        </span>
      )}
      {!!data.values && (
        <div className="flex flex-col gap-2.5">
          {Object.entries(data.values).map(([label, value], index) => (
            <div key={`${label}_${index}`} className="flex flex-col gap-0.5">
              <div className="w-full flex items-center justify-between">
                <span className="text-sm truncate whitespace-pre-line text-typography-darkgrey">{label}</span>
                <CopyButtonInline value={value} />
              </div>
              <span className="text-sm break-words whitespace-pre-line">
                <Linkify>{value}</Linkify>
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default TextValueCard;
